/**
 * Derelict reactivation, eligibility (plan S15; spec §16.2, §16.3 step 5, §16.5 limits).
 *
 * - **Seeded per POI:** every M3 `derelict` POI is read as a First Rush station (`FR-###`); its `frClass`,
 *   `restorable` flag, Holdfast marker and family, hazards, squatters and designation come from the galaxy seed, the
 *   system index and the POI id (`deriveSeed`), so the same galaxy always gives the same stations. Story-placed ship
 *   hulks (the Column Nine field) are excluded by id. The flag is read on the survey, never shown before.
 * - **Exclusions** (`reactivation.json eligibility.exclusions`): the Harrow (always, canon), the Column Nine field,
 *   Okafor's lab until Mission 3 completes, the guardian and hidden bases (not derelicts), the Gate Cluster.
 * - **One restart per system per owner:** the player (title or corporation) once per system; each hand-over
 *   recipient once per system.
 * - **Reboot parameters:** the M3 relay reboot table by region (`reach-relink.json reboot`), guardians when a
 *   tetherstone vein is within its hop rule.
 *
 * Pure and event-driven (survey time), never per frame.
 */
import { createRng, nextFloat } from '../../core/rng'
import { deriveSeed, intRange, pickWeighted } from '../../galaxy/seed'
import type { Galaxy, Poi, RegionId, StarSystem } from '../../galaxy/types'
import type { RelinkConfig } from '../../relink/config'
import { rebootSchedule, veinWithinHops, type ScheduledSpawn } from '../../relink/rules'
import type { FactionId } from '../../strategic/types'
import type { ReactivationConfig, ReactivationExclusion } from '../schemas'
import type { FrClass, MarkerRegion, StoryReactivation, StoryState } from '../types'

/** What blocks a derelict from restoration (an exclusion id, or a seeded or kind reason). */
export type IneligibleReason = ReactivationExclusion | 'notDerelict' | 'notRestorable'

/** Story-placed POIs the galaxy can't know (the integration lane places them, plan S37/S38). */
export interface StorySites {
  /** The Harrow's POI id (`HomeCluster.harrowPoiId`), or null when the layout has none. */
  harrowPoiId: string | null
  /** The Column Nine field's hulks (ship hulks, a memorial or stripped). */
  columnNinePoiIds: readonly string[]
  /** Okafor's lab derelict, or null before it is placed. */
  okaforLabPoiId: string | null
}

/** The Harrow's id as the M3 home cluster names it (`homeCluster.ts`: `<home system id>-harrow`). */
export function harrowPoiIdOf(galaxy: Pick<Galaxy, 'homeSystemId'>): string {
  return `${galaxy.homeSystemId}-harrow`
}

/** Story sites with only what the galaxy knows (the Harrow); the integration lane adds the rest. */
export function defaultStorySites(galaxy: Pick<Galaxy, 'homeSystemId'>): StorySites {
  return {
    harrowPoiId: harrowPoiIdOf(galaxy),
    columnNinePoiIds: [],
    okaforLabPoiId: null,
  }
}

export const SQUATTERS = ['none', 'wreckers', 'holdfastFamily'] as const
export type Squatters = (typeof SQUATTERS)[number]

/** A derelict read as a First Rush station: the seeded facts the survey card shows. */
export interface FrStation {
  poiId: string
  systemId: string
  systemIndex: number
  region: RegionId
  markerRegion: MarkerRegion
  /** `FR-###`. */
  designation: string
  frClass: FrClass
  /** Seeded (placeholder 40%); exclusions apply on top (`ineligibleReason`). */
  restorable: boolean
  /** A Holdfast generational claim marker's family, or null (spec §16.6). */
  markerFamily: string | null
  /** The reactor section's coolant anchor must be vented (spec §16.3 step 2). */
  coolantHazard: boolean
  squatters: Squatters
  /** M3's hidden flag (a deep-scan reveals the POI first). */
  hidden: boolean
}

/** Mission 3 (The Transferred Team) is complete: Okafor's lab becomes eligible (spec §16.2). */
export function m3Complete(story: Pick<StoryState, 'missions'>): boolean {
  return story.missions.some((m) => m.id === 'm3' && m.state === 'complete')
}

/** The marker share's region: the descendant enclaves are their own row (spec §16.6). */
export function markerRegionOf(system: Pick<StarSystem, 'region' | 'archetype'>): MarkerRegion {
  return system.archetype === 'descendant-enclave' ? 'enclave' : system.region
}

/** `FR-###` from a seeded number in the data range, never the Harrow's. */
function designationOf(n: number, cfg: ReactivationConfig): string {
  return `FR-${n === cfg.harrow.designation ? cfg.survey.designation.max : n}`
}

/**
 * The seeded First Rush facts for one derelict POI. Okafor's lab is always a restorable lab (a clinic, spec §16.6).
 * The draws run in a fixed order so a data change to one share never reshuffles the others' streams.
 */
export function frStationOf(
  galaxy: Pick<Galaxy, 'seed'>,
  system: Pick<StarSystem, 'id' | 'index' | 'region' | 'archetype'>,
  poi: Pick<Poi, 'id' | 'hidden'>,
  cfg: ReactivationConfig,
  sites?: Pick<StorySites, 'okaforLabPoiId'>,
): FrStation {
  const rng = createRng(deriveSeed(galaxy.seed, 'reactivation', system.index, poi.id))
  const e = cfg.eligibility
  const markerRegion = markerRegionOf(system)
  const restorableRoll = nextFloat(rng)
  const classPick = pickWeighted(rng, e.classShares)
  const markerRoll = nextFloat(rng)
  const family = cfg.holdfast.families[intRange(rng, 0, cfg.holdfast.families.length - 1)]
  const coolantHazard = nextFloat(rng) < cfg.survey.coolantHazardShare
  const squatRoll = nextFloat(rng)
  const designation = designationOf(intRange(rng, cfg.survey.designation.min, cfg.survey.designation.max), cfg)
  const lab = sites?.okaforLabPoiId === poi.id
  const markerFamily = markerRoll < cfg.holdfast.markerShare[markerRegion] ? family : null
  const squatters: Squatters = markerFamily !== null ? 'holdfastFamily' : squatRoll < cfg.survey.squatterShare[system.region] ? 'wreckers' : 'none'
  return {
    poiId: poi.id,
    systemId: system.id,
    systemIndex: system.index,
    region: system.region,
    markerRegion,
    designation,
    frClass: lab ? 'lab' : classPick,
    restorable: lab || restorableRoll < e.restorableShare[system.region],
    markerFamily,
    coolantHazard,
    squatters,
    hidden: poi.hidden,
  }
}

/** Every M3 derelict POI in the galaxy as a First Rush station, in system then POI order. */
export function frStations(galaxy: Galaxy, cfg: ReactivationConfig, sites?: Pick<StorySites, 'okaforLabPoiId'>): FrStation[] {
  const out: FrStation[] = []
  for (const system of galaxy.systems) {
    for (const poi of system.pois) if (poi.kind === 'derelict') out.push(frStationOf(galaxy, system, poi, cfg, sites))
  }
  return out
}

/** The story inputs the exclusions read. */
export interface EligibilityContext {
  sites: StorySites
  /** `m3Complete(story)`. */
  m3Complete: boolean
}

/**
 * Why `poi` can't be restored, or null when it can. The Harrow is refused whatever the data says (canon: a live
 * Watch post); every other exclusion applies when `reactivation.json` lists it.
 */
export function ineligibleReason(
  system: Pick<StarSystem, 'region'>,
  poi: Pick<Poi, 'id' | 'kind'>,
  station: Pick<FrStation, 'restorable'> | null,
  ctx: EligibilityContext,
  cfg: ReactivationConfig,
): IneligibleReason | null {
  const listed = (x: ReactivationExclusion): boolean => cfg.eligibility.exclusions.includes(x)
  if (poi.id === ctx.sites.harrowPoiId) return 'harrow'
  if (listed('column_nine') && ctx.sites.columnNinePoiIds.includes(poi.id)) return 'column_nine'
  if (poi.kind === 'hidden-base') return listed('hidden_bases') ? 'hidden_bases' : 'notDerelict'
  if (poi.kind !== 'derelict') return 'notDerelict'
  if (poi.id === ctx.sites.okaforLabPoiId) {
    if (listed('okafor_lab_before_m3') && !ctx.m3Complete) return 'okafor_lab_before_m3'
    return null
  }
  if (listed('gate_cluster') && system.region === 'gate') return 'gate_cluster'
  if (station === null || !station.restorable) return 'notRestorable'
  return null
}

/** The galaxy's restorable First Rush stations now (exclusions applied). */
export function eligibleStations(galaxy: Galaxy, ctx: EligibilityContext, cfg: ReactivationConfig): FrStation[] {
  const out: FrStation[] = []
  for (const system of galaxy.systems) {
    for (const poi of system.pois) {
      if (poi.kind !== 'derelict') continue
      const station = frStationOf(galaxy, system, poi, cfg, ctx.sites)
      if (ineligibleReason(system, poi, station, ctx, cfg) === null) out.push(station)
    }
  }
  return out
}

/** Who owns a restart: the player (title or corporation) or the hand-over recipient. */
export type RestartOwner = 'player' | FactionId

/** The owner of a registered (or pending) record; a record without a registrant counts as the player's attempt. */
export function restartOwnerOf(r: Pick<StoryReactivation, 'registrant' | 'recipient'>): RestartOwner {
  return r.registrant === 'handover' && r.recipient !== undefined ? r.recipient : 'player'
}

/**
 * One restart per system per owner (spec §16.2): false when `owner` already holds a live restart (not reverted, not
 * this POI) in `systemId`. `systemOfPoi` maps a POI id to its system (the galaxy index).
 */
export function restartSlotFree(
  reactivations: readonly StoryReactivation[],
  systemOfPoi: (poiId: string) => string | undefined,
  poiId: string,
  systemId: string,
  owner: RestartOwner,
): boolean {
  for (const r of reactivations) {
    if (r.poiId === poiId || r.state === 'reverted' || r.registrant === undefined) continue
    if (systemOfPoi(r.poiId) === systemId && restartOwnerOf(r) === owner) return false
  }
  return true
}

/** POI id → system id over the galaxy (built once per call site; survey time only). */
export function poiSystemIndex(galaxy: Galaxy): Map<string, string> {
  const out = new Map<string, string>()
  for (const s of galaxy.systems) for (const p of s.pois) out.set(p.id, s.id)
  return out
}

/** The restart's reboot defence (spec §16.3 step 5): the M3 relay reboot table by region. */
export interface RebootParams {
  seconds: number
  guardians: boolean
  schedule: ScheduledSpawn[]
}

export function rebootParams(galaxy: Galaxy, system: Pick<StarSystem, 'id' | 'region'>, relink: RelinkConfig): RebootParams {
  const seconds = relink.reboot.seconds[system.region]
  const guardians = veinWithinHops(galaxy, system.id, relink.reboot.guardians.hops)
  return {
    seconds,
    guardians,
    schedule: rebootSchedule(system.region, seconds, guardians, relink),
  }
}
